/**
 * Initialize Heap Analytics
 *
 * Loads the Heap script and sets up method stubs for queuing events
 * before the script fully loads. Only essential methods are stubbed.
 *
 * @see https://developers.heap.io/reference/client-side-apis-overview
 */
export function initializeHeap(): void {
  const heapId = import.meta.env.VITE_HEAP_APIKEY;

  if (!heapId) {
    console.warn(
      '[InitHeap] ⚠️ Heap ID is not configured for this environment.'
    );
    return;
  }

  // Already initialized
  if (globalThis.heap?.appid) {
    return;
  }

  try {
    // Initialize the heap queue and ready callbacks
    globalThis.heapReadyCb = globalThis.heapReadyCb || [];
    globalThis.heap = globalThis.heap || ({} as Heap);
    globalThis.heap.appid = heapId;

    // Load the Heap script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://cdn.heapanalytics.com/js/heap-${heapId}.js`;

    document.head.appendChild(script);

    // Create stub methods that queue calls until Heap is ready
    // Only the methods we actually use in the app
    const methodsToStub = [
      'identify',
      'track',
      'resetIdentity',
      'addUserProperties',
    ] as const;

    type HeapMethod = (typeof methodsToStub)[number];

    for (const method of methodsToStub) {
      if (!globalThis.heap[method]) {
        const stubFn = (...args: unknown[]): void => {
          globalThis.heapReadyCb.push({
            name: method,
            fn: () =>
              (globalThis.heap[method] as (...a: unknown[]) => void)?.(...args),
          });
        };
        (globalThis.heap as Record<HeapMethod, typeof stubFn>)[method] = stubFn;
      }
    }
  } catch (error) {
    console.error('Error initializing Heap:', error);
  }
}
